/**
 * Contracts mode for the Options Tape: premium summed per contract across every matching print.
 *
 * Same request shape as the retired Top Premium Board (`/api/options-flow/aggregate` with
 * `group_by=contract`, top 150 by premium), but built from the Options Tape query so every
 * server filter on the bar — side, type, strikes, DTE, size, expiry, class, cap tier, window —
 * applies to the grouping, not just the five the old board passed.
 */

import { getApiUrl } from '../../config/api';
import { getAuthToken } from '../../utils/authUtils';
import { buildFlowTapeQuery, FLOW_AGGREGATE_ENDPOINT, FlowTapeQuery } from '../../utils/flowTapeApi';
import { parseOccContract } from '../../utils/liveTapeTiles';
import { FlowVerdictLabels, verdictFromAggregate } from '../../utils/flowVerdictLabels';

export const CONTRACT_BOARD_LIMIT = 150;

export interface ContractRow {
  contract: string;
  ticker: string;
  optionType: 'C' | 'P';
  strike: number;
  expiry: string;
  dte: number | null;
  premium: number;
  prints: number;
  contracts: number;
  buyPremium: number | null;
  sellPremium: number | null;
  verdict: FlowVerdictLabels;
  /** Shape the custom rules read, keyed like a tape print. */
  raw: Record<string, unknown>;
}

function dteFrom(expiry: string): number | null {
  const t = Date.parse(`${expiry}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return Math.round((t - today.getTime()) / 86400000);
}

export async function fetchContractRows(
  q: FlowTapeQuery,
  limit = CONTRACT_BOARD_LIMIT,
  signal?: AbortSignal,
): Promise<ContractRow[]> {
  const qs = buildFlowTapeQuery(q);
  qs.set('group_by', 'contract');
  qs.set('limit', String(Math.max(1, Math.min(limit, 500))));
  qs.delete('sort_by');
  qs.delete('sort_dir');
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(getApiUrl(`${FLOW_AGGREGATE_ENDPOINT}?${qs.toString()}`), {
    headers,
    cache: 'no-store',
    signal,
  });
  if (!res.ok) throw new Error(`Contract board request failed (${res.status})`);
  const json = await res.json();
  const buckets = (Array.isArray(json?.buckets) ? json.buckets : []) as Record<string, unknown>[];
  const out: ContractRow[] = [];
  for (const b of buckets) {
    const contract = String(b.bucket ?? '');
    const parsed = parseOccContract(contract);
    if (!parsed) continue;
    const num = (v: unknown): number | null => {
      const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
      return Number.isFinite(n) ? n : null;
    };
    const premium = num(b.premium) ?? 0;
    const contracts = num(b.contracts) ?? 0;
    const dte = dteFrom(parsed.expiry);
    out.push({
      contract,
      ticker: parsed.ticker,
      optionType: parsed.side,
      strike: parsed.strike,
      expiry: parsed.expiry,
      dte,
      premium,
      prints: num(b.prints) ?? 0,
      contracts,
      buyPremium: num(b.buy_premium),
      sellPremium: num(b.sell_premium),
      verdict: verdictFromAggregate(b as Parameters<typeof verdictFromAggregate>[0]),
      raw: {
        Contract: contract,
        Ticker: parsed.ticker,
        'Option Type': parsed.side,
        Strike: parsed.strike,
        'Expiry Date': parsed.expiry,
        DTE: dte,
        Premium: premium,
        Volume: contracts,
      },
    });
  }
  return out;
}
